import { VALIDATION_LIMITS, FILE_UPLOAD } from './constants';
/**
 * Validate email format
 */
export function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}
/**
 * Validate username format and length
 */
export function isValidUsername(username) {
    const { MIN_LENGTH, MAX_LENGTH } = VALIDATION_LIMITS.USERNAME;
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    return (username.length >= MIN_LENGTH &&
        username.length <= MAX_LENGTH &&
        usernameRegex.test(username));
}
/**
 * Validate password strength
 */
export function isValidPassword(password) {
    const { MIN_LENGTH, MAX_LENGTH } = VALIDATION_LIMITS.PASSWORD;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    return (password.length >= MIN_LENGTH &&
        password.length <= MAX_LENGTH &&
        hasUppercase &&
        hasLowercase &&
        hasNumber);
}
/**
 * Get password strength score (0-4)
 */
export function getPasswordStrength(password) {
    let score = 0;
    if (password.length >= 8)
        score++;
    if (/[a-z]/.test(password))
        score++;
    if (/[A-Z]/.test(password))
        score++;
    if (/\d/.test(password))
        score++;
    if (/[^a-zA-Z0-9]/.test(password))
        score++;
    return Math.min(score, 4);
}
/**
 * Validate file type and size
 */
export function isValidFile(file, type) {
    const allowedTypes = FILE_UPLOAD.ALLOWED_TYPES[type];
    const maxSize = FILE_UPLOAD.MAX_SIZE[type];
    return allowedTypes.includes(file.type) && file.size <= maxSize;
}
/**
 * Validate URL format
 */
export function isValidUrl(url) {
    try {
        new URL(url);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Validate social media URL
 */
export function isValidSocialUrl(url, platform) {
    if (!isValidUrl(url))
        return false;
    const platformDomains = {
        instagram: ['instagram.com', 'www.instagram.com'],
        youtube: ['youtube.com', 'www.youtube.com', 'youtu.be'],
        tiktok: ['tiktok.com', 'www.tiktok.com'],
        twitter: ['twitter.com', 'www.twitter.com', 'x.com', 'www.x.com'],
    };
    const domains = platformDomains[platform];
    if (!domains)
        return true; // Allow unknown platforms
    try {
        const urlObj = new URL(url);
        return domains.includes(urlObj.hostname);
    }
    catch {
        return false;
    }
}
/**
 * Sanitize HTML content
 */
export function sanitizeHtml(html) {
    // Basic HTML sanitization - remove script tags and dangerous attributes
    return html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/on\w+="[^"]*"/gi, '')
        .replace(/javascript:/gi, '');
}
/**
 * Validate recipe step duration
 */
export function isValidDuration(seconds) {
    return seconds > 0 && seconds <= 3600; // Max 1 hour
}
/**
 * Validate temperature range
 */
export function isValidTemperature(celsius) {
    return celsius >= 0 && celsius <= 100; // 0-100°C
}
/**
 * Validate coffee to water ratio
 */
export function isValidRatio(ratio) {
    const ratioRegex = /^\d+(\.\d+)?$/;
    if (!ratioRegex.test(ratio))
        return false;
    const numericRatio = parseFloat(ratio);
    return numericRatio >= 1 && numericRatio <= 30; // 1:1 to 1:30
}
/**
 * Validate rating value
 */
export function isValidRating(rating) {
    return rating >= 1 && rating <= 5 && Number.isInteger(rating * 2); // Allow half stars
}
/**
 * Clean and validate text input
 */
export function cleanText(text) {
    return text.trim().replace(/\s+/g, ' ');
}
/**
 * Validate text length
 */
export function isValidTextLength(text, minLength, maxLength) {
    const cleanedText = cleanText(text);
    return cleanedText.length >= minLength && cleanedText.length <= maxLength;
}
